/**
 * Clientes Page - Gerenciamento de Clientes
 */

import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { ClientesTable } from "@/components/cliente/ClientesTable";
import { ClienteFormDialog } from "@/components/cliente/ClienteFormDialog";
import { ClienteViewDialog } from "@/components/cliente/ClienteViewDialog";
import { ClienteDeleteDialog } from "@/components/cliente/ClienteDeleteDialog";
import { Cliente } from "@/types/cliente";
import { ClienteFormData } from "@/schemas/clienteSchema";
import { clientesAdapter } from "@/data";
import { useToast } from "@/hooks/use-toast";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

export default function Clientes() {
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState<Cliente | null>(null);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [actionLoading, setActionLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadClientes();
  }, []);

  const loadClientes = async () => {
    try {
      setLoading(true);
      const response = await clientesAdapter.list();
      setClientes(response.items);
    } catch (error) {
      console.error("Error loading clientes:", error);
      setClientes([]); // Garante UI consistente
      
      // Toast APENAS para erros REAIS (RLS, network, etc.)
      if (error && typeof error === 'object') {
        const errorMessage = (error as any)?.message || '';
        const errorCode = (error as any)?.code || '';
        
        // Ignora "banco vazio" ou resultados vazios normais
        if (
          !errorMessage.toLowerCase().includes('no rows') &&
          !errorMessage.toLowerCase().includes('empty') &&
          errorCode !== 'PGRST116' // Supabase: no rows
        ) {
          toast({
            title: "Erro ao carregar clientes",
            description: errorMessage || "Não foi possível carregar a lista de clientes.",
            variant: "destructive",
          });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormMode("create");
    setSelectedCliente(null);
    setFormOpen(true);
  };

  const handleEdit = (cliente: Cliente) => {
    setFormMode("edit");
    setSelectedCliente(cliente);
    setFormOpen(true);
  };

  const handleView = (cliente: Cliente) => {
    setSelectedCliente(cliente);
    setViewOpen(true);
  };

  const handleDelete = (cliente: Cliente) => {
    setSelectedCliente(cliente);
    setDeleteOpen(true);
  };

  const handleFormSubmit = async (data: ClienteFormData) => {
    try {
      setActionLoading(true);

      if (formMode === "create") {
        await clientesAdapter.create(data);
        toast({
          title: "Cliente criado",
          description: "O cliente foi criado com sucesso.",
        });
      } else if (selectedCliente) {
        await clientesAdapter.update(selectedCliente.id_cliente, data);
        toast({
          title: "Cliente atualizado",
          description: "O cliente foi atualizado com sucesso.",
        });
      }

      loadClientes();
      setFormOpen(false);
    } catch (error: any) {
      console.error("Erro ao salvar cliente:", error);
      toast({
        title: "Erro ao salvar",
        description: error?.message || "Não foi possível salvar o cliente.",
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedCliente) return;

    try {
      setActionLoading(true);
      console.log("🗑️ Deletando cliente:", selectedCliente);
      await clientesAdapter.delete(selectedCliente.id_cliente);
      
      toast({
        title: "✅ Cliente excluído",
        description: "O cliente foi excluído com sucesso.",
      });

      loadClientes();
      setDeleteOpen(false);
    } catch (error: any) {
      console.error("❌ Erro ao deletar cliente:", error);
      
      let errorMessage = "Não foi possível excluir o cliente.";
      
      if (error?.message?.includes("vendas associadas")) {
        errorMessage = error.message;
      } else if (error?.message?.includes("policy") || error?.code === "42501") {
        errorMessage = "Você não tem permissão para excluir clientes.";
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Erro ao excluir",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Clientes</h1>
          <p className="text-muted-foreground">
            Gerencie os clientes do sistema
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" text="Carregando clientes..." />
          </div>
        ) : (
          <ClientesTable
            clientes={clientes}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onCreate={handleCreate}
          />
        )}
      </div>

      <ClienteFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        mode={formMode}
        cliente={selectedCliente}
        onSubmit={handleFormSubmit}
        isLoading={actionLoading}
      />

      <ClienteViewDialog
        open={viewOpen}
        onOpenChange={setViewOpen}
        cliente={selectedCliente}
      />

      <ClienteDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        cliente={selectedCliente}
        onConfirm={handleConfirmDelete}
        isLoading={actionLoading}
      />
    </Layout>
  );
}
